import { useEffect, useRef, useState } from 'react';
import { STORE_INFO } from '@/mocks/content';

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function CtaSection() {
  const { ref, inView } = useInView(0.2);

  return (
    <section id="cta" className="relative overflow-hidden py-28 md:py-40">
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=luxury%20lounge%20bar%20dark%20elegant%20atmosphere%20warm%20golden%20bokeh%20lights%20deep%20black%20background%20sophisticated%20Japanese%20night%20club%20premium%20ambiance%20cinematic%20wide%20angle%20moody&width=1920&height=800&seq=cta001&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0F]/70 via-[#0A0A0F]/60 to-[#0A0A0F]/80" />

      {/* Smooth Veil overlay */}
      <div
        className="absolute inset-0 transition-opacity duration-1500"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.05) 0%, transparent 70%)',
          opacity: inView ? 1 : 0,
        }}
      />

      <div ref={ref} className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <div
          className="transition-all duration-1000 ease-out"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(30px)',
          }}
        >
          <p
            className="text-[#C9A84C] text-xs tracking-[0.6em] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            RESERVATION
          </p>
          <h2
            className="text-white text-3xl md:text-5xl font-bold tracking-widest mb-6"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            ご予約・空席確認
          </h2>

          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="w-12 h-px bg-[#C9A84C]/50" />
            <span
              className="text-white/50 text-xs tracking-widest"
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              お電話またはInstagram DMで
            </span>
            <span className="w-12 h-px bg-[#C9A84C]/50" />
          </div>

          <p
            className="text-white/60 text-sm leading-[2] mb-10 tracking-widest"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            初めてのお客様も大歓迎です。<br />
            ご不明な点はお電話またはInstagram DMにてお問い合わせください。
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={STORE_INFO.telHref}
              className="group inline-flex items-center gap-4 px-10 py-5 bg-[#C9A84C] text-[#0A0A0F] text-sm font-bold tracking-widest hover:bg-[#E8C96A] transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-lg group-hover:animate-pulse" />
              </div>
              お電話でご予約・空席確認
            </a>
            <a
              href={STORE_INFO.instagramDm}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="group inline-flex items-center gap-4 px-10 py-5 border border-[#C9A84C]/70 text-[#C9A84C] text-sm font-bold tracking-widest hover:bg-[#C9A84C]/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-instagram-line text-lg group-hover:scale-110 transition-transform duration-300" />
              </div>
              インスタDMで問い合わせ
            </a>
          </div>

          <p
            className="text-white/30 text-xs mt-6 tracking-widest"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            営業時間 {STORE_INFO.hours}
          </p>
        </div>
      </div>
    </section>
  );
}
